import { useState } from "react";
import { Plus } from "lucide-react";
import { useParams } from "react-router-dom";

import { useAppDispatch } from "../../../app/store";
import { downloadFraudElements } from "../logic/fraudElementsThunk";

import { cn } from "../../../lib/utils";
import CreateFraudElementsModal from "./createFraudElementModal";
import { showErrorToast } from "../../../lib/toast";
import { useAppSelector } from "../../../app/hooks";
import { selectFraudElementCount } from "../logic/fraudElementsSlice";
import DownloadFormatMenu, { DownloadFormatOption } from "../../../components/ui/download/DownloadFormatMenu";
import { ExportFormat } from "../../../api/download";
import { selectPartiesWithSCNAction } from "../../recommendations/logic/ActionsAgainstPerpetratorsSlice";

const buttonBaseClass = "inline-flex h-9 items-center justify-center gap-2 rounded-[8px] px-4 py-2 text-[14px] font-medium leading-5 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]";

const FraudElementsHeader = () => {
  const dispatch = useAppDispatch();

  const { id: caseId } = useParams<{ id: string }>();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const fraudElementCount = useAppSelector(selectFraudElementCount);
  const scnParties = useAppSelector(selectPartiesWithSCNAction);

  const handleDownload = async (format: ExportFormat) => {
    if (!caseId) return;

    setIsDownloading(true);

    try {
      await dispatch(
        downloadFraudElements({
          case_id: caseId,
          format,
        }),
      ).unwrap();
    } catch {
      showErrorToast("Failed to Download. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadOptions: DownloadFormatOption[] = [
    {
      format: ExportFormat.DOCX,
      label: "Microsoft Word (.docx)",
    },
  ];

  return (
    <>
      <div className="rounded-t-[14px] bg-[#F9FAFB]">
        <div className="flex items-center justify-between px-[10px] py-[6px] font-[Inter]">
          <div className="flex flex-1 items-center py-[6px]">
            <span className="text-[18px] font-medium leading-7 text-[#0A0A0A] ">
              Elements of Fraud
            </span>
          </div>

          <div className="flex items-center gap-3">
            {fraudElementCount > 0 && (
              <DownloadFormatMenu
                variant="button"
                loading={isDownloading}
                onSelect={handleDownload}
                options={downloadOptions}
              />
            )}

            {scnParties.length > 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className={cn(
                  buttonBaseClass,
                  "bg-gradient-to-b from-[#788AF7] to-[#3551F3] text-[#FAFAFA]",
                )}
              >
                <Plus size={16} />
                <span>Create Fraud Element</span>

              </button>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateFraudElementsModal
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </>
  );
};

export default FraudElementsHeader;